// frontend/src/components/payment/paymentBrand.js
//
// The payment pages' look, declared once - composed ONLY from existing tokens:
// the card-on-file "Medallion" family (`cardOnFile/cardOnFileBrand.js`: navy
// action, white and mint grounds, navy hairlines, the one focus ring), the
// brand wash (brand/tokens.json `gradient.wash`) and the theme's warning ramp
// for the failure page's calm attention tone. No NEW colour is introduced:
// white is the theme's `colors.background.default` (as WHITE below, also the
// transparent end of the hero's fade), and the card's navy shadows repeat
// cardOnFileBrand's own rgba(14, 22, 40, ...) literals (BRAND.navy).
//
// GRADIENT DIRECTION (CLAUDE.md): every gradient DECORATION (the medallion
// ring, the hairline under the title) runs bright on the physical LEFT and
// dark on the physical RIGHT, and is never mirrored for `dir` - each is written
// with a physical `to right` / x1=0 -> x2=1, never with a logical direction.
// The hero's mint -> blush WASH is a large field; it keeps the direction the
// brand defines for it (113deg, mint left, blush right - brand/tokens.json:
// "never mirrored"), in Hebrew and English alike.
//
// MOTION: the one moving part is the success check's single stroke (no loop,
// no pulse); it stops under the app's single motion switch
// (`html.lf-motion-off`) and under `prefers-reduced-motion`, and is written so
// that NOT running leaves the drawn check on screen. No text ever waits at
// opacity 0 for an animation.

import { alpha } from "@mui/material/styles";
import { keyframes } from "@emotion/react";

import { colors, radii, typography } from "../../theme/tokens";
import { BRAND, focusRingSx, navyPillSx } from "../cardOnFile/cardOnFileBrand";

export { BRAND, focusRingSx };

/** The white of every white ground here - the theme token, never a literal. */
const WHITE = colors.background.default;

/** The two medallion tones: the brand's calm teal, and a calm amber. */
export const TONES = Object.freeze({
  brand: Object.freeze({
    ringFrom: BRAND.ringBright,
    ringTo: BRAND.motto,
    glyph: BRAND.motto,
    disc: `radial-gradient(circle at 50% 38%, ${WHITE} 0%, ${BRAND.mintGround} 72%, ${BRAND.washMint} 100%)`,
    halo: alpha(BRAND.motto, 0.16),
  }),
  attention: Object.freeze({
    ringFrom: alpha(colors.warning.main, 0.35),
    ringTo: colors.warning.dark,
    glyph: colors.warning.dark,
    disc: `radial-gradient(circle at 50% 38%, ${WHITE} 0%, ${alpha(colors.warning.main, 0.06)} 72%, ${alpha(
      colors.warning.main,
      0.14,
    )} 100%)`,
    halo: alpha(colors.warning.dark, 0.14),
  }),
});

/** Text inks: navy, and navy steps for secondary lines (AA on white and wash). */
export const INK = Object.freeze({
  strong: BRAND.navy,
  body: alpha(BRAND.navy, 0.78),
  meta: alpha(BRAND.navy, 0.66),
});

/** The motion gate every animated element carries. */
export const MOTION_OFF_SX = Object.freeze({
  "html.lf-motion-off &": { animation: "none" },
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
});

/** The check's stroke length (the polyline below), for the one draw. */
export const CHECK_PATH_LENGTH = 30;

export const drawStroke = keyframes`
  from { stroke-dashoffset: ${CHECK_PATH_LENGTH}; }
  to   { stroke-dashoffset: 0; }
`;

/** The card: white slip, navy hairline, soft navy shadow, centred, max 560. */
export const CARD_SX = Object.freeze({
  position: "relative",
  width: "100%",
  maxWidth: 560,
  boxSizing: "border-box",
  borderRadius: { xs: "22px", sm: "26px" },
  border: `1px solid ${BRAND.hairline}`,
  background: WHITE,
  boxShadow: "0 18px 44px rgba(14, 22, 40, 0.08), 0 2px 6px rgba(14, 22, 40, 0.04)",
  // No entrance animation, deliberately: the card always REPLACES a skeleton
  // of itself (the route's chunk fallback, then the pending state), and a card
  // fading in from nothing over its own skeleton reads as a blink.
  overflow: "hidden",
});

/** The hero field: the brand wash, faded into the card's white below it. */
export const HERO_SX = Object.freeze({
  position: "relative",
  textAlign: "center",
  px: { xs: 2.5, sm: 5 },
  pt: { xs: 4, sm: 5 },
  pb: { xs: 2.5, sm: 3 },
  background: [
    `linear-gradient(180deg, ${alpha(WHITE, 0)} 0%, ${alpha(WHITE, 0.35)} 45%, ${WHITE} 92%)`,
    `linear-gradient(113deg, ${BRAND.washMint}, ${BRAND.washBlush})`,
  ].join(", "),
});

/** The card body under the hero. */
export const BODY_SX = Object.freeze({
  px: { xs: 2, sm: 4 },
  pb: { xs: 3, sm: 3.5 },
});

/** The page title (the one h1). */
export const TITLE_SX = Object.freeze({
  m: 0,
  mt: { xs: 2.5, sm: 3 },
  fontFamily: typography.fontFamily.heading,
  fontWeight: 700,
  fontSize: { xs: "28px", sm: "34px" },
  lineHeight: 1.2,
  letterSpacing: "-0.01em",
  color: INK.strong,
  textWrap: "balance",
  outline: "none",
});

export const SUBTITLE_SX = Object.freeze({
  m: 0,
  mt: 1.25,
  mx: "auto",
  maxWidth: "42ch",
  fontFamily: typography.fontFamily.body,
  fontSize: { xs: "15px", sm: "16px" },
  lineHeight: 1.6,
  color: INK.body,
  // Balanced lines, so a sentence break never strands a word or two on the
  // next line (progressive enhancement - ignored where unsupported).
  textWrap: "balance",
});

export const TAGLINE_SX = Object.freeze({
  m: 0,
  mt: 1,
  mx: "auto",
  maxWidth: "44ch",
  fontFamily: typography.fontFamily.body,
  fontSize: { xs: "14px", sm: "14.5px" },
  fontWeight: 600,
  lineHeight: 1.55,
  // 5.6:1 on white - the one teal that may carry text.
  color: BRAND.motto,
  textWrap: "balance",
});

/** Short accent hairline under the title - physical: bright LEFT, dark RIGHT. */
export const hairlineSx = (tone) => ({
  width: 56,
  height: "2px",
  borderRadius: "2px",
  mx: "auto",
  mt: 1.75,
  background: `linear-gradient(to right, ${tone.ringFrom}, ${tone.ringTo})`,
});

// --- Actions ---------------------------------------------------------------

const ACTION_BASE_SX = {
  boxSizing: "border-box",
  minHeight: 48,
  px: 3,
  py: 1.25,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  textDecoration: "none",
  fontFamily: typography.fontFamily.heading,
  fontSize: "15px",
  lineHeight: 1.3,
  whiteSpace: "normal",
};

/** Primary: the solid navy pill of the card-on-file family. */
export const PRIMARY_ACTION_SX = Object.freeze({
  ...navyPillSx,
  ...ACTION_BASE_SX,
  whiteSpace: "normal",
});

/** Secondary: a white pill with a navy hairline and navy text. */
export const SECONDARY_ACTION_SX = Object.freeze({
  ...ACTION_BASE_SX,
  borderRadius: "999px",
  border: `1px solid ${alpha(BRAND.navy, 0.18)}`,
  background: WHITE,
  color: BRAND.navy,
  cursor: "pointer",
  fontWeight: 600,
  transition: "background-color 0.2s ease, border-color 0.2s ease",
  "@media (hover: hover)": {
    "&:hover": { background: BRAND.mintGround, borderColor: alpha(BRAND.navy, 0.28) },
  },
  ...focusRingSx,
});

/** Tertiary: an underlined text link (the third action of a state). */
export const TERTIARY_ACTION_SX = Object.freeze({
  display: "inline-flex",
  alignItems: "center",
  minHeight: 44,
  px: 1,
  fontFamily: typography.fontFamily.body,
  fontSize: "14px",
  fontWeight: 600,
  color: BRAND.navy,
  textDecoration: "underline",
  textDecorationColor: alpha(BRAND.navy, 0.35),
  textUnderlineOffset: "3px",
  borderRadius: "6px",
  "@media (hover: hover)": {
    "&:hover": { textDecorationColor: BRAND.navy },
  },
  ...focusRingSx,
});

// --- Receipt ---------------------------------------------------------------

/** The receipt section: the Settings sub-card, in the mint family. */
export const RECEIPT_SECTION_SX = Object.freeze({
  p: { xs: 1.5, sm: 2 },
  background: BRAND.mintGround,
  border: `1px solid ${BRAND.mintRule}`,
  borderRadius: `${radii.md}px`,
});

export const RECEIPT_HEADING_SX = Object.freeze({
  m: 0,
  mb: 1.25,
  px: 0.5,
  fontFamily: typography.fontFamily.heading,
  fontSize: "15px",
  fontWeight: 700,
  lineHeight: 1.4,
  color: INK.strong,
});

/** The white list of rows inside the section (Settings row radius). */
export const RECEIPT_LIST_SX = Object.freeze({
  m: 0,
  background: WHITE,
  border: `1px solid ${BRAND.mintRule}`,
  borderRadius: `${radii.row}px`,
  overflow: "hidden",
});

export const RECEIPT_ROW_SX = Object.freeze({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1.5,
  px: { xs: 1.5, sm: 2 },
  py: 1.25,
  minHeight: 48,
  boxSizing: "border-box",
  "& + &": { borderTop: `1px solid ${BRAND.mintRule}` },
});

export const RECEIPT_LABEL_SX = Object.freeze({
  m: 0,
  flexShrink: 0,
  fontFamily: typography.fontFamily.body,
  fontSize: "14px",
  lineHeight: 1.45,
  color: INK.meta,
});

export const RECEIPT_VALUE_SX = Object.freeze({
  m: 0,
  minWidth: 0,
  textAlign: "end",
  fontFamily: typography.fontFamily.body,
  fontSize: "14px",
  fontWeight: 600,
  lineHeight: 1.45,
  color: INK.strong,
  fontVariantNumeric: "tabular-nums",
  overflowWrap: "anywhere",
});

/** A content-sized pill (plan, status). */
export const PILL_SX = Object.freeze({
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  px: "12px",
  py: "4px",
  borderRadius: `${radii.full}px`,
  fontFamily: typography.fontFamily.heading,
  fontSize: "13px",
  fontWeight: 700,
  lineHeight: 1.3,
  letterSpacing: "0.02em",
});

/** The order reference: small, monospace, an LTR island. */
export const REFERENCE_SX = Object.freeze({
  m: 0,
  mt: 2.5,
  textAlign: "center",
  fontFamily: typography.fontFamily.body,
  fontSize: "13px",
  lineHeight: 1.5,
  color: INK.meta,
  overflowWrap: "anywhere",
});

export const REFERENCE_CODE_SX = Object.freeze({
  fontFamily: typography.fontFamily.mono,
  fontSize: "13px",
  color: INK.body,
  letterSpacing: "0.02em",
  unicodeBidi: "isolate",
});

/** The failure page's message box: a calm amber tint, navy text. */
export const NOTICE_SX = Object.freeze({
  m: 0,
  px: { xs: 2, sm: 2.5 },
  py: { xs: 1.75, sm: 2 },
  borderRadius: `${radii.md}px`,
  background: alpha(colors.warning.main, 0.07),
  border: `1px solid ${alpha(colors.warning.main, 0.28)}`,
  textAlign: "center",
  fontFamily: typography.fontFamily.body,
  fontSize: { xs: "14.5px", sm: "15px" },
  lineHeight: 1.65,
  color: INK.strong,
  textWrap: "pretty",
});

export const HELP_SX = Object.freeze({
  m: 0,
  mt: 2,
  textAlign: "center",
  fontFamily: typography.fontFamily.body,
  fontSize: "14px",
  lineHeight: 1.6,
  color: INK.body,
  textWrap: "pretty",
});
